import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  /** @type {number} */
  #lastTouchToggleAt = 0;

  /**
   * Keep both the inert attribute and property in sync for cross-browser support.
   * Some iOS Safari versions don't reliably reflect property-only changes in CSS selectors.
   * @param {HTMLElement} element
   * @param {boolean} isInert
   */
  #setInertState(element, isInert) {
    if (isInert) {
      element.setAttribute('inert', '');
    } else {
      element.removeAttribute('inert');
    }

    if ('inert' in element) {
      element.inert = isInert;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const { disclosureTrigger: trigger } = this.refs;
    if (trigger) {
      trigger.addEventListener('click', this.#handleClickToggle);
      trigger.addEventListener('touchend', this.#handleTouchToggle, { passive: false });
    }
  }

  disconnectedCallback() {
    const { disclosureTrigger: trigger } = this.refs;
    if (trigger) {
      trigger.removeEventListener('click', this.#handleClickToggle);
      trigger.removeEventListener('touchend', this.#handleTouchToggle);
    }
    super.disconnectedCallback();
  }

  /**
   * Direct click handler for reliability on browsers where delegated handlers can be flaky.
   * @param {MouseEvent} event
   */
  #handleClickToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.toggleDisclosure(event);
  };

  /**
   * iOS Safari can miss delegated click handlers in some composited/masked containers.
   * Handle touchend directly on the trigger and suppress the follow-up synthetic click.
   * @param {TouchEvent} event
   */
  #handleTouchToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#lastTouchToggleAt = Date.now();
    this.toggleDisclosure();
  };

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = (event) => {
    if (event instanceof MouseEvent && Date.now() - this.#lastTouchToggleAt < 500) {
      event.preventDefault();
      return;
    }

    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    trigger.setAttribute('aria-expanded', String(!expanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );
    this.#setInertState(content, expanded);
  };
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
